import { getStatusCodeFromError } from '@/api'
import { isDesktop } from '@/app/platform'
import { useAuthentication } from '@alexandrainst/plana-react-api'
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister'
import {
  QueryClient,
  QueryClientConfig,
  QueryClientProvider,
} from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { compress, decompress } from 'lz-string'
import { PropsWithChildren, useMemo } from 'react'
import { isUsingPersistentQueryCache } from './configuration'

const queryDefaultOptionsDesktop = {
  defaultOptions: {
    queries: {
      staleTime: Infinity,
      gcTime: 1000 * 60 * 60 * 24, // 24 hours
      refetchInterval: 5000,
      refetchIntervalInBackground: false,
      throwOnError: true,
    },
  },
} satisfies QueryClientConfig

export const ApplicationQueryClientProvider = ({
  children,
}: PropsWithChildren) => {
  const {
    logout,
    refresh,
    state: { loginRequestInProgress },
  } = useAuthentication()

  const queryDefaultOptionsMobile = useMemo(
    () =>
      ({
        defaultOptions: {
          queries: {
            staleTime: Infinity,
            // Previous gcTime value was 24 hours: 1000 * 60 * 60 * 24
            gcTime: 1000 * 60 * 60 * 2, // 2 hours
            // refetchInterval: 1000,
            // refetchIntervalInBackground: true
            retry: (failureCount, error) => {
              const statusCode = getStatusCodeFromError(error)
              if (statusCode === 401 || statusCode === 403) {
                if (failureCount === 0) {
                  console.error(
                    'Unauthorized or forbidden error, refreshing token',
                    failureCount
                  )
                  refresh()
                  return true
                } else if (loginRequestInProgress && failureCount < 3) {
                  console.error(
                    'Login request in progress, retrying',
                    failureCount
                  )
                  return true
                } else {
                  console.error(
                    'Unauthorized or forbidden error, not retrying',
                    failureCount
                  )
                  logout()
                  return false
                }
              } else {
                return failureCount < 3
              }
            },
            throwOnError: (error: unknown) => {
              const statusCode = getStatusCodeFromError(error)
              if (statusCode === 401 || statusCode === 403) {
                return true
              } else {
                return true
              }
            },
          },
        },
      }) satisfies QueryClientConfig,
    [loginRequestInProgress, logout, refresh]
  )

  const defaultQueryOptions = isDesktop()
    ? queryDefaultOptionsDesktop
    : queryDefaultOptionsMobile
  const queryClient = new QueryClient(defaultQueryOptions)
  if (isUsingPersistentQueryCache) {
    console.info('Using persisted queries')
    const persister = createSyncStoragePersister({
      storage: window.localStorage,
      throttleTime: 10 * 1000,
      serialize: data => compress(JSON.stringify(data)),
      deserialize: data => JSON.parse(decompress(data) ?? data),
    })
    return (
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{ persister }}
      >
        {children}
        <ReactQueryDevtools initialIsOpen={false} />
      </PersistQueryClientProvider>
    )
  } else
    return (
      <QueryClientProvider client={queryClient}>
        {children}
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    )
}
